"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LocalStorageKeyStore = void 0;
var getKeyMetadata_1 = require("../Helpers/getKeyMetadata");
var LocalStorageFacade_1 = require("./LocalStorageFacade");
/**
 * KeyStore for the Web Storage API :
 * https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API.
 * Once instantiated and configured, pass it to the `KeyManager` contructor to
 * handle the storage of encrypted keys.
 * ```js
 * const localKeyStore = new KeyManagerPlugins.LocalStorageKeyStore();
 * localKeyStore.configure({ storage: localStorage });
 * const keyManager = new KeyManager({
 *     keyStore: localKeyStore
 *  });
 * ```
 */
var LocalStorageKeyStore = /** @class */ (function () {
    function LocalStorageKeyStore() {
        this.name = "LocalStorageKeyStore";
        this.keyStore = new LocalStorageFacade_1.LocalStorageFacade();
    }
    /**
     * The configuration is where the storage engine is set up and configured.
     * It must follow the Storage interface :
     * https://developer.mozilla.org/en-US/docs/Web/API/Storage).
     * In the DOM environment it can be `localStorage` or `sessionStorage`.
     * In a Node environment, there are some substitution libraries available,
     * *node-localstorage* for instance.
     * If not set, the calls to the other methods will fail.
     * @param {LocalStorageConfigParams} params A configuration object.
     * @param {Storage} params.storage The Storage instance. Required.
     * @param {string} [params.prefix] The prefix for the names in the storage.
     * @returns {Promise} resolved data
     */
    LocalStorageKeyStore.prototype.configure = function (params) {
        try {
            this.keyStore.configure(params);
            return Promise.resolve();
        }
        catch (e) {
            return Promise.reject(e);
        }
    };
    LocalStorageKeyStore.prototype.storeKeys = function (keys) {
        var _this = this;
        // We can't store keys if they're already there
        var invalidKeys = keys.filter(function (encryptedKey) { return _this.keyStore.hasKey(encryptedKey.id); });
        if (invalidKeys.length) {
            return Promise.reject("Some keys were already stored in the keystore: ".concat(invalidKeys
                .map(function (k) { return k.id; })
                .join(", ")));
        }
        var keysMetadata = keys.map(function (encryptedKey) {
            _this.keyStore.setKey(encryptedKey.id, encryptedKey);
            return (0, getKeyMetadata_1.getKeyMetadata)(encryptedKey);
        });
        return Promise.resolve(keysMetadata);
    };
    LocalStorageKeyStore.prototype.updateKeys = function (keys) {
        var _this = this;
        // we can't update keys if they're already stored
        var invalidKeys = keys.filter(function (encryptedKey) { return !_this.keyStore.hasKey(encryptedKey.id); });
        if (invalidKeys.length) {
            return Promise.reject("Some keys couldn't be found in the keystore: ".concat(invalidKeys
                .map(function (k) { return k.id; })
                .join(", ")));
        }
        var keysMetadata = keys.map(function (encryptedKey) {
            _this.keyStore.setKey(encryptedKey.id, encryptedKey);
            return (0, getKeyMetadata_1.getKeyMetadata)(encryptedKey);
        });
        return Promise.resolve(keysMetadata);
    };
    LocalStorageKeyStore.prototype.loadKey = function (id) {
        var key = this.keyStore.getKey(id);
        if (!key) {
            return Promise.reject(id);
        }
        return Promise.resolve(key);
    };
    LocalStorageKeyStore.prototype.removeKey = function (id) {
        if (!this.keyStore.hasKey(id)) {
            return Promise.reject(id);
        }
        var metadata = (0, getKeyMetadata_1.getKeyMetadata)(this.keyStore.getKey(id));
        this.keyStore.removeKey(id);
        return Promise.resolve(metadata);
    };
    LocalStorageKeyStore.prototype.loadAllKeys = function () {
        return Promise.resolve(this.keyStore.getAllKeys());
    };
    return LocalStorageKeyStore;
}());
exports.LocalStorageKeyStore = LocalStorageKeyStore;
